import {
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  createElement,
  useEffect,
  useState,
} from 'react';
import Loader from 'react-components/workflows/loader';
import styled from 'styled-components';
import { generateDatadogTrackingProps } from 'toolkit/environment/environment/user-monitoring';
import { themeColors } from '../styles/colors';
import NoDataMessage from './NoDataMessage';
import StandardSortableHeaders from './standard-sortable-headers';
import { HeaderCellConfig } from './types/header.types';
import { MenuItemConfig } from './types/menu.types';
import { Order, PaginationBounds, SortParameters } from './types/table.types';
import type { StyledTableContainer, StyledTablePagination } from '../../types/mui-styled-components';

const Index = styled.div`
  position: relative;
`;

const StandardTableContainer = styled(TableContainer)<{ component?: React.ElementType; elevation?: number }>`
  position: relative;
  overflow-x: initial;
  overflow-y: none;
` as StyledTableContainer;

const Pagination = styled(TablePagination)<{ component?: React.ElementType }>`
  position: sticky;
  bottom: -16px;
  background-color: ${themeColors.containerLight};
` as StyledTablePagination;

export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

/**
 * Interface defines the props needed to implement a
 * StandardTable.
 *
 * @prop data: T[] - array of data containing the data used to populate the table
 * @prop dataRowElement: - React functional component that data will be passed into to render each row
 * @prop headerCells: HeaderCellConfig<T>[] - configuration defining the column headers for the table
 * @prop totalRecords: number - required number of total records in all pages; required for pagination
 * @prop rowsPerPageOptions: number[] <optional> [default: 10, 25, 50] - the number of records to display
 *    per page
 * @prop isLoading: boolean <optional> [default: false] -
 * @prop stickyHeader: boolean <optional> [default: true] - determines if the table header is sticky
 * @prop ariaLabel: string <optional> - a label adding the context to a screen reader indicating the
 *    type of data in the table
 * @prop dispatchAction: (params: SortParameters<T>) => - the action to call when dispatching to retreive records
 */
interface StandardTableProps<T, V extends string> {
  data: T[];
  dataRowElement: React.FunctionComponent<{
    data: T;
    openAdditionalMenu: (event: React.MouseEvent<HTMLElement>, data: T) => void;
  }>;
  menuItems?: MenuItemConfig<T>[];
  headerCells: HeaderCellConfig<V>[];
  totalRecords: number;
  rowsPerPageOptions?: number[];
  bounds: PaginationBounds;
  isLoading?: boolean;
  stickyHeader?: boolean;
  ariaLabel?: string;
  rowKeyGenerator?: (entry: T) => string;
  dispatchAction: (params: SortParameters<V>) => void;
  setBounds: Dispatch<SetStateAction<PaginationBounds>>;
}

/**
 * StandardTable is a wrapper component around an implementation
 * of the React MUI table element.
 */
const StandardTable = <T, V extends string>({
  data,
  dataRowElement,
  menuItems,
  headerCells,
  totalRecords,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  bounds,
  isLoading = false,
  stickyHeader = true,
  ariaLabel,
  rowKeyGenerator = (entry: T) => entry['id'],
  dispatchAction,
  setBounds,
}: StandardTableProps<T, V>) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>(null);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement>(null);
  const open = Boolean(menuAnchor);

  const [selectedDataElement, setSelectedDataElement] = useState<T>(null);

  useEffect(() => {
    // trigger an initial set of sort params so that the parent component knows what to do
    void handleDispatch(orderBy, order, bounds.pageSize, bounds.pageStart);
  }, []);

  function openMenu(event: React.MouseEvent<HTMLElement>, data: T) {
    setSelectedDataElement(data);
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setSelectedDataElement(null);
    setMenuAnchor(null);
  }

  async function handleDispatch(
    orderBy: string,
    orderDirection: Order,
    pageSize: number,
    pageStart: number
  ) {
    await dispatchAction({
      orderBy,
      orderDirection,
      pageSize,
      pageStart,
    });
  }

  async function handleSort(
    _event: React.MouseEvent<unknown>,
    property: string
  ) {
    const isAsc = orderBy === property && order === 'asc';
    const orderDirection = isAsc ? 'desc' : 'asc';
    setOrder(orderDirection);
    setOrderBy(property);

    await handleDispatch(
      property,
      orderDirection,
      bounds.pageSize,
      bounds.pageStart
    );
  }

  async function handlePageChange(pageNumber: number) {
    setBounds(previousBounds => {
      return { ...previousBounds, pageStart: pageNumber };
    });
    await handleDispatch(orderBy, order, bounds.pageSize, pageNumber);
  }

  async function handleRowsPerPageChange(pageSize: number) {
    setBounds({ pageStart: 0, pageSize });
    await handleDispatch(orderBy, order, pageSize, 0);
  }

  return (
    <Index>
      <StandardTableContainer component={Paper} elevation={0}>
        <Fragment>
          {isLoading && <Loader />}
          {!isLoading && (
            <Table
              stickyHeader={stickyHeader}
              aria-label={ariaLabel ? `${ariaLabel} table` : 'table'}
            >
              {headerCells?.length && (
                <colgroup>
                  {headerCells.map((colDef, i) => {
                    if (colDef.width) {
                      return (
                        <col key={`coldefwidth-${i}`} width={colDef.width} />
                      );
                    }
                    return <col key={`coldef-${i}`} />;
                  })}
                </colgroup>
              )}
              <StandardSortableHeaders
                headerCells={headerCells}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleSort}
              />
              <TableBody>
                {!!data?.length && (
                  <Fragment>
                    {data?.map(entry =>
                      createElement(dataRowElement, {
                        key: rowKeyGenerator(entry),
                        data: entry,
                        openAdditionalMenu: (e, data) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openMenu(e, data);
                        },
                      })
                    )}
                  </Fragment>
                )}
                {!data?.length && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={headerCells.length}>
                      <NoDataMessage message="No records found" />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {!!menuItems?.length && (
                <Menu
                  anchorEl={menuAnchor}
                  open={open}
                  onClose={closeMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {menuItems.reduce((aggregate, item) => {
                    const hidden =
                      typeof item.hidden === 'function'
                        ? item.hidden(selectedDataElement)
                        : item.hidden;

                    if (hidden) return aggregate;

                    return [
                      ...aggregate,
                      <MenuItem
                        key={item.display}
                        sx={{ minWidth: '246px' }}
                        onClick={() => {
                          item.callback(selectedDataElement);
                          closeMenu();
                        }}
                        disabled={
                          typeof item.disabled === 'function'
                            ? item.disabled(selectedDataElement)
                            : item.disabled
                        }
                        {...generateDatadogTrackingProps(item.ddTrackingName)}
                      >
                        {item.display}
                      </MenuItem>,
                    ];
                  }, [])}
                </Menu>
              )}
            </Table>
          )}
          <Pagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={totalRecords || 0}
            rowsPerPage={bounds.pageSize || rowsPerPageOptions[0]}
            page={totalRecords > 0 ? bounds.pageStart || 0 : 0}
            onPageChange={(_event, page) => {
              void handlePageChange(page);
            }}
            onRowsPerPageChange={event => {
              void handleRowsPerPageChange(parseInt(event.target.value));
            }}
          />
        </Fragment>
      </StandardTableContainer>
    </Index>
  );
};

export default StandardTable;
